import { FC } from "react";
import { useNavigate } from "react-router";

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { LogOut, CircleUser, X } from "lucide-react";

import LogoIcon from "../../ui/assets/svg/vviinn-logo.svg?react";

import { MenuPrimary } from "./MenuPrimary";
import { MenuAdmin } from "./MenuAdmin";
import { ITEM_DEFAULT_STYLES } from "./MenuItems/MenuItem";
import { useAuth } from "../../auth";

import { useAppSelector } from "../../store";
import { useBlockScroll } from "../../app/hooks/useBlockScroll";
import { MENU_ICON_STYLE } from "./styles";
import {
  ENTER_TO,
  menuTransition,
  opacityTransition,
} from "../../ui/layout/Transition/constants";
import { Button } from "../../ui/layout/molecules/Button";
import { BORDER_STYLES } from "../../ui/layout/detailedPages/styles";

type MenuMobileProps = {
  sidebarOpen: boolean;
  closeMenuMobile: () => void;
};

export const MenuMobile: FC<React.PropsWithChildren<MenuMobileProps>> = ({
  sidebarOpen,
  closeMenuMobile,
}: MenuMobileProps) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const userId = useAppSelector((state) => state.user.id);
  const userProfileLink = `/users/show/${userId}`;
  useBlockScroll(sidebarOpen);
  return (
    <Transition show={sidebarOpen} as="div">
      <Dialog
        as="div"
        className="fixed inset-0 z-40 flex lg:hidden"
        onClose={() => closeMenuMobile()}
      >
        <TransitionChild
          as="div"
          enter="transition-opacity ease-linear duration-300"
          leave="transition-opacity ease-linear duration-300"
          {...opacityTransition}
        >
          <div className="fixed inset-0 bg-stone-600/75" />
        </TransitionChild>
        <TransitionChild
          as="div"
          enterFrom="-translate-x-full"
          enterTo={ENTER_TO}
          leaveFrom={ENTER_TO}
          leaveTo="-translate-x-full"
          {...menuTransition}
          className="w-full h-full"
        >
          <DialogPanel className="relative flex w-full h-full max-w-xs flex-1 flex-col bg-stone-50 dark:bg-black">
            <TransitionChild
              as="div"
              enter="ease-in-out duration-300"
              leave="ease-in-out duration-300"
              {...opacityTransition}
            >
              <Button
                title="Close menu"
                className={`h-10 w-10 absolute -right-12 top-2 p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white text-white`}
                buttonType="iconOnly"
              >
                <X />
              </Button>
            </TransitionChild>
            <div className="h-0 flex-1 overflow-y-auto text-sm">
              <div className="flex h-16 shrink-0 items-center bg-stone-900 px-4 py-4 dark:border-b dark:border-r dark:border-stone-800 dark:bg-black">
                <div className="mx-auto flex shrink-0 items-center px-4">
                  <LogoIcon width="112" height="28" />
                </div>
              </div>
              <div className="flex grow flex-col py-2">
                <MenuPrimary
                  closeMenuMobile={closeMenuMobile}
                  isMobile={true}
                />
                <MenuAdmin closeMenuMobile={closeMenuMobile} />
                <div
                  className={`mt-1 w-full space-y-1 border-t px-2 py-2 ${BORDER_STYLES}`}
                >
                  <button
                    className={ITEM_DEFAULT_STYLES}
                    onClick={() => {
                      navigate(userProfileLink);
                      closeMenuMobile();
                    }}
                  >
                    <CircleUser className={MENU_ICON_STYLE} />
                    Profile
                  </button>

                  <button
                    className={ITEM_DEFAULT_STYLES}
                    onClick={() => logout()}
                  >
                    <LogOut className={MENU_ICON_STYLE} />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
        <div className="w-14 shrink-0">
          {/* Force sidebar to shrink to fit close icon */}
        </div>
      </Dialog>
    </Transition>
  );
};
